import { useQuery } from '@tanstack/react-query';
import { Search, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  buildClubPlayerRows,
  collectClubPlayerIds,
  filterClubPlayerRows,
  useClubCompetitionsQuery,
  useCompetitionEntriesQuery,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import {
  Alert,
  Avatar,
  Badge,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/shared/ui';

/**
 * Zawodnicy klubu — lista osób, które grały w jego rozgrywkach.
 *
 * API nie ma endpointu „zawodnicy klubu”, więc listę składamy ze zgłoszeń do
 * poszczególnych rozgrywek (`GET /api/competitions/{id}/participants`). Skutek
 * uboczny tego źródła: kto nigdy nie zgłosił się do żadnej rozgrywki, na liście
 * się nie pojawi — mówi o tym przypis pod tabelą.
 */
export function ClubPlayersPage() {
  const { t } = useTranslation('common');
  const { club, clubId } = useSelectedClub();

  const competitionsQuery = useClubCompetitionsQuery(clubId);
  const competitions = useMemo(() => competitionsQuery.data ?? [], [competitionsQuery.data]);
  const entriesQuery = useCompetitionEntriesQuery(competitions);

  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);

  // Nazwiska jednym batchem dla wszystkich osób ze wszystkich rozgrywek.
  const playerIds = useMemo(() => collectClubPlayerIds(entries).sort(), [entries]);
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', playerIds.join(',')],
    queryFn: () => getUsersByIds(playerIds),
    enabled: playerIds.length > 0,
  });
  const profiles = useMemo(
    () => new Map((profilesQuery.data ?? []).map((profile) => [profile.id, profile])),
    [profilesQuery.data],
  );

  const [search, setSearch] = useState('');
  const rows = useMemo(() => buildClubPlayerRows(entries, profiles), [entries, profiles]);
  const visible = useMemo(() => filterClubPlayerRows(rows, search), [rows, search]);

  const header = (
    <PageHeader
      title={t('clubPlayers.title')}
      description={
        club
          ? t('clubPlayers.description', { club: club.name })
          : t('clubPlayers.descriptionNoClub')
      }
    />
  );

  if (!clubId) {
    return (
      <>
        {header}
        <EmptyState
          icon={<Users />}
          title={t('clubPlayers.noClubTitle')}
          description={t('clubPlayers.noClubDescription')}
          className="min-h-[16rem]"
        />
      </>
    );
  }

  if (competitionsQuery.isError || entriesQuery.isError) {
    return (
      <>
        {header}
        <Alert tone="danger">{t('clubPlayers.loadError')}</Alert>
      </>
    );
  }

  // Klub bez rozgrywek nie odpala zapytania o zgłoszenia, więc na samo
  // `entriesQuery.isPending` czekać nie można — wisiałoby w nieskończoność.
  const loading =
    competitionsQuery.isPending || (competitions.length > 0 && entriesQuery.isPending);

  return (
    <div className="flex flex-col gap-5">
      {header}

      <Input
        inputSize="sm"
        type="search"
        aria-label={t('clubPlayers.search')}
        placeholder={t('clubPlayers.search')}
        leadingIcon={<Search />}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="w-64"
      />

      {loading && <Skeleton className="h-64 w-full" />}

      {!loading && rows.length === 0 && (
        <EmptyState
          icon={<Users />}
          title={t('clubPlayers.emptyTitle')}
          description={t('clubPlayers.emptyDescription')}
          className="min-h-[16rem]"
        />
      )}

      {!loading && rows.length > 0 && visible.length === 0 && (
        <EmptyState
          icon={<Search />}
          title={t('clubPlayers.noMatchTitle')}
          description={t('clubPlayers.noMatchDescription')}
          className="min-h-[16rem]"
        />
      )}

      {visible.length > 0 && (
        <TableContainer>
          <Table className="[&_td]:px-3 [&_th]:px-3">
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-12">
                  {t('clubPlayers.columns.index')}
                </TableHeaderCell>
                <TableHeaderCell>{t('clubPlayers.columns.player')}</TableHeaderCell>
                <TableHeaderCell className="w-28 text-right">
                  {t('clubPlayers.columns.competitions')}
                </TableHeaderCell>
                <TableHeaderCell>{t('clubPlayers.columns.lastCompetition')}</TableHeaderCell>
                <TableHeaderCell className="w-28">
                  {t('clubPlayers.columns.status')}
                </TableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visible.map((row, index) => (
                <TableRow key={row.userId}>
                  <TableCell className="text-fg-subtle text-sm tabular-nums">{index + 1}</TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <Avatar name={row.name} size="sm" colorful />
                      <div className="min-w-0">
                        <p className="text-fg-strong truncate text-sm font-semibold">{row.name}</p>
                        {row.email && (
                          <p className="text-fg-muted truncate text-xs">{row.email}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-fg text-right text-sm tabular-nums">
                    {row.competitionCount}
                  </TableCell>

                  <TableCell>
                    <Link
                      to={`/turnieje/${row.lastCompetitionId}`}
                      className="text-fg hover:text-fg-strong text-sm underline-offset-2 hover:underline"
                    >
                      {row.lastCompetitionName}
                    </Link>
                  </TableCell>

                  <TableCell>
                    <Badge tone={row.playing ? 'brand' : 'neutral'} size="sm">
                      {row.playing ? t('clubPlayers.playing') : t('clubPlayers.inactive')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TableFooter>
            {t('clubPlayers.footer', { shown: visible.length, total: rows.length })}
          </TableFooter>
        </TableContainer>
      )}

      <p className="text-fg-subtle text-xs">{t('clubPlayers.sourceNote')}</p>
    </div>
  );
}
